import React from 'react';
import { toast } from 'react-toastify';

const MAX_DETAILS = 5;
const installedFlag = '__rizeFetchErrorHandlingInstalled';

const friendlyFieldName = (name) => String(name)
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

function collectMessages(value, path = '', output = []) {
  if (output.length >= MAX_DETAILS || value === null || value === undefined) return output;

  if (typeof value === 'string') {
    const message = value.trim();
    if (message && !/<[a-z][\s\S]*>/i.test(message)) {
      output.push(path ? `${path}: ${message}` : message);
    }
    return output;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      const itemPath = isObject(item) ? `${path || 'Item'} ${index + 1}` : path;
      collectMessages(item, itemPath, output);
    });
    return output;
  }

  if (isObject(value)) {
    const ignored = new Set(['success', 'status', 'status_code', 'code']);
    Object.entries(value).forEach(([key, item]) => {
      if (ignored.has(key)) return;
      const isHeadline = ['detail', 'message', 'error', 'non_field_errors'].includes(key);
      collectMessages(item, isHeadline ? path : [path, friendlyFieldName(key)].filter(Boolean).join(' › '), output);
    });
  }

  return output;
}

function uniqueMessages(payload) {
  return [...new Set(collectMessages(payload))].slice(0, MAX_DETAILS);
}

function requestKey(url, method = 'GET') {
  try {
    const parsed = new URL(url, window.location.origin);
    const stablePath = parsed.pathname.replace(/\/\d+(?=\/|$)/g, '/:id');
    return `api:${method.toUpperCase()}:${stablePath}`;
  } catch (_) {
    return `api:${method.toUpperCase()}:request`;
  }
}

function messageForStatus(status, payload, method) {
  const backendMessages = uniqueMessages(payload);

  if (status === 400 || status === 422) {
    return {
      title: method === 'GET' ? 'We could not load this information' : 'Please check the information entered',
      details: backendMessages.length ? backendMessages : ['Some information is missing or invalid.'],
    };
  }
  if (status === 401) return { title: 'Your session has expired', details: ['Please sign in again to continue.'] };
  if (status === 403) return { title: 'You do not have access', details: backendMessages.length ? backendMessages : ['Contact an administrator if you think this is a mistake.'] };
  if (status === 404) return { title: 'Information not found', details: backendMessages.length ? backendMessages : ['It may have been moved or deleted. Refresh and try again.'] };
  if (status === 409) return { title: 'This change conflicts with newer information', details: backendMessages.length ? backendMessages : ['Refresh the page and try again.'] };
  if (status === 413) return { title: 'The selected file is too large', details: ['Choose a smaller file and try again.'] };
  if (status === 429) return { title: 'Too many requests', details: ['Please wait a moment before trying again.'] };
  if (status >= 500) return { title: 'Our service is temporarily unavailable', details: ['Your information was not saved. Please try again shortly.'] };

  return {
    title: 'We could not complete that request',
    details: backendMessages.length ? backendMessages : ['Please try again.'],
  };
}

function ToastMessage({ title, details }) {
  return (
    <div className="rize-toast-body" role="alert">
      <div className="rize-toast-body__text">
        <p className="rize-toast-body__title">{title}</p>
        {details?.length === 1 && <p className="rize-toast-body__detail">{details[0]}</p>}
        {details?.length > 1 && (
          <ul className="rize-toast-body__list">
            {details.map((detail, index) => <li key={`${index}-${detail}`}>{detail}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

export function notifyError(error, options = {}) {
  if (error?.name === 'AbortError') return;

  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  const status = options.status || error?.status || error?.response?.status || 0;
  const method = (options.method || 'GET').toUpperCase();
  const payload = options.payload || error?.response?.data;
  const fallback = status ? messageForStatus(status, payload, method) : options.network || offline ? {
    title: offline ? 'You are offline' : 'Connection problem',
    details: [offline ? 'Reconnect to the internet and try again.' : 'Please check your connection and try again.'],
  } : {
    title: 'Something went wrong',
    details: ['Please try again in a moment. If it keeps happening, refresh the page.'],
  };
  const title = options.title || fallback.title;
  const details = options.details || fallback.details;

  toast.error(<ToastMessage title={title} details={details} />, {
    toastId: options.toastId || 'app:unexpected-error',
    autoClose: Math.min(5500 + Math.max(0, details.length - 1) * 1000, 10000),
    icon: false,
  });
}

export function notifyWarning(title, detail, toastId = title) {
  toast.warning(<ToastMessage title={title} details={detail ? [detail] : []} />, { toastId });
}

// When the observer has shown a toast for a failed request, the thunk that made
// it rejects a moment later and its caller would toast the same failure again.
// Callers pass the time their action started: only a request that failed after
// that belongs to the action. A validation rejection made no request, so it is
// still shown even if some unrelated request failed just before.
let lastRequestErrorAt = 0;

export const requestErrorShownSince = (startedAt) => lastRequestErrorAt >= startedAt;

// Loading an onboarding step the user has not saved yet returns 404 — that is an
// empty form, not an error. Only the step loads (/api/onboarding/<step>) are
// covered; reports, downloads and every other 404 still show a message.
const isUnsavedStepLoad = (url, method, status) => {
  if (method !== 'GET' || status !== 404) return false;
  try {
    return /^\/api\/onboarding\/[a-z_]+\/?$/i.test(new URL(url, window.location.origin).pathname);
  } catch (_) {
    return false;
  }
};

async function payloadFrom(response) {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('json')) return null;
  return response.json().catch(() => null);
}

// Silent re-auth hook. The auth layer registers a function that, given the
// bearer token a request failed with, resolves to a fresh token (or null when
// it can't help). Kept as an injected hook so this module stays auth-agnostic.
let unauthorizedHandler = null;

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

// Maps the bearer token a caller attached to the newest one. Callers read the
// token from Redux, which is deliberately left untouched on renewal (dozens of
// effects refetch when it changes), so the swap to the fresh token happens here.
let tokenResolver = null;

export function setTokenResolver(fn) {
  tokenResolver = fn;
}

function withCurrentToken(input, init) {
  if (!tokenResolver) return init;
  const token = bearerTokenOf(input, init);
  if (!token) return init;
  const current = tokenResolver(token);
  if (!current || current === token) return init;
  const headers = requestHeaders(input, init);
  headers.set('Authorization', `Bearer ${current}`);
  return { ...init, headers };
}

function requestHeaders(input, init) {
  if (init.headers) return new Headers(init.headers);
  if (typeof Request !== 'undefined' && input instanceof Request) return new Headers(input.headers);
  return new Headers();
}

function bearerTokenOf(input, init) {
  const auth = requestHeaders(input, init).get('Authorization') || '';
  return auth.startsWith('Bearer ') ? auth.slice(7) : null;
}

// An expired token comes back as 401, but DRF answers failed authentication
// with 403 when the auth class sends no WWW-Authenticate header — so a 403
// whose message is about credentials counts too. Permission 403s don't.
async function isAuthFailure(response) {
  if (response.status === 401) return true;
  if (response.status !== 403) return false;
  const payload = await payloadFrom(response.clone());
  const detail = uniqueMessages(payload).join(' ');
  return /credential|not authenticated|authentication|token/i.test(detail);
}

// When the token expired mid-session, renew it silently and replay the request
// once, so the caller just sees the successful response.
// A body that lives inside a Request object, or is a stream, is consumed by the
// first attempt and cannot be sent again. (The app sends strings / FormData.)
function canReplay(input, init) {
  // Request.body isn't exposed in every browser, so judge by method instead.
  if (typeof Request !== 'undefined' && input instanceof Request && !['GET', 'HEAD'].includes(input.method)) return false;
  if (typeof ReadableStream !== 'undefined' && init.body instanceof ReadableStream) return false;
  return true;
}

async function retryWithFreshToken(nativeFetch, input, init, response) {
  if (!unauthorizedHandler || !canReplay(input, init)) return response;
  const failedToken = bearerTokenOf(input, init);
  if (!failedToken || !(await isAuthFailure(response))) return response;

  const freshToken = await unauthorizedHandler(failedToken).catch(() => null);
  if (!freshToken || freshToken === failedToken) return response;

  const headers = requestHeaders(input, init);
  headers.set('Authorization', `Bearer ${freshToken}`);
  return nativeFetch(input, { ...init, headers });
}

/**
 * Installs one non-invasive observer around fetch. It never changes a response,
 * so existing Redux thunks continue to work, while backend, Logic App and file
 * request failures get the same user-facing treatment. Pass
 * `{ errorToast: false }` for an intentionally silent/optional request; the
 * custom option is removed before native fetch.
 */
export function installGlobalFetchErrorHandling() {
  if (window[installedFlag]) return;
  window[installedFlag] = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    const method = (init.method || input?.method || 'GET').toUpperCase();
    const shouldNotify = init.errorToast !== false;
    const { errorToast, ...callerInit } = init;
    const nativeInit = withCurrentToken(input, callerInit);

    try {
      let response = await nativeFetch(input, nativeInit);
      if (!response.ok) {
        response = await retryWithFreshToken(nativeFetch, input, nativeInit, response);
      }
      if (shouldNotify && !response.ok && !isUnsavedStepLoad(url, method, response.status)) {
        lastRequestErrorAt = Date.now();
        const payload = await payloadFrom(response.clone());
        notifyError(null, {
          status: response.status,
          payload,
          method,
          toastId: requestKey(url, method),
        });
      }
      return response;
    } catch (error) {
      if (shouldNotify && error?.name !== 'AbortError') {
        lastRequestErrorAt = Date.now();
        notifyError(error, { method, network: true, toastId: requestKey(url, method) });
      }
      throw error;
    }
  };
}
